import { _isEmptyString, _notNil } from '@/littledash.ts';
import { type FieldErrors, type Resolver, type UseFormReturn } from 'react-hook-form';
import type { FC } from 'react';
import { useCallback } from 'react';
import {
  type MeasurementFuse,
  type MeasurementInputNode,
  type MeasurementMapFormContext,
  type MeasurementMapFormData,
  type MeasurementNode,
  type MeasurementOutputNode,
  type MeasurementOutputOption,
  type TargetPresetData,
} from '@/components/Studies/Templates/CreateDataTable/steps/MapMeasurements.utils.tsx';
import { type StudySettingsCalculationV1 } from '@/model/Preset.model.ts';
import { type StudyApiId } from '@/model/Study.model.ts';
import { type AnimalApiId } from '@/model/Animal.model.ts';
import type Fuse from 'fuse.js';
import Icon from '@/components/UI/Icon';
import type { Icon as IconType } from '@/constants/Icons.ts';
import { type MeasurementMapping } from '@/components/Studies/Templates/CreateDataTable/CreateDataTableFromTemplate.model.tsx';
import {
  ANIMAL_FLUID_INTAKE,
  ANIMAL_FOOD_INTAKE,
} from '@/components/Workflow/Show/Views/Consumption/Consumption.utils.ts';

export type FromPresetData = {
  outputs: Map<string, MeasurementOutputNode>;
  inputs: Map<string, MeasurementInputNode>;
};

export const addToPresetOption = {
  type: 'output',
  slug: 'addToPreset',
  value: 'addToPreset',
  name: 'Add to preset',
  unit: '',
  label: '- Add to preset -',
  children: {},
  fuse: {},
} as const;

export const dropMeasurementOption = {
  type: 'input',
  slug: 'dropMeasurement',
  value: 'dropMeasurement',
  name: 'Drop measurement',
  unit: '',
  label: '- Drop measurement -',
  children: {},
  fuse: {},
} as const;

export const measurementFuseSearchName = (slug: string, name: string, unit: string | null | undefined) =>
  _isEmptyString(unit ?? '') ? `${slug} - ${name}` : `${slug} - ${name} (${unit})`;
export const measurementLabel = (name: string, unit: string | null | undefined): string =>
  _isEmptyString(unit ?? '') ? name : `${name} (${unit})`;

export const generateSourcePresetData = (
  calculations: Array<StudySettingsCalculationV1>,
  targetPresetData: TargetPresetData
): FromPresetData => {
  const targetPresetSlugs: Set<string> = extractTargetPresetSlugs(targetPresetData);
  const dependencyMap = calculations.reduce(
    (acc, calc) => {
      calc.dependencies.forEach((child) => {
        if (!acc[child]) {
          acc[child] = [];
        }
        acc[child].push(calc.id);
      });
      return acc;
    },
    {} as Record<string, string[]>
  );
  const { outputs, inputs } = calculations.reduce(
    (acc, calc) => {
      acc.outputs.set(calc.id, {
        type: 'output',
        slug: calc.id,
        name: calc.name,
        unit: calc.unit as string,
        label: measurementLabel(calc.name, calc.unit),
        search: measurementFuseSearchName(calc.id, calc.name, calc.unit),
        targetContainsSlug: outputOrChildrenContainsSlug(calc, targetPresetSlugs),
        children: new Map<string, MeasurementInputNode>(),
        calculatedMeasurement: calc.dependencies.length > 0,
        dependencies: calc.dependencies,
        dependencyParents: dependencyMap[calc.id] ?? [],
      });
      if (calc.measurements?.at(0)?.id !== calc.id) {
        const parentSlug = calc.id;
        const parent = acc.outputs.get(parentSlug) as MeasurementOutputNode;
        calc.measurements.forEach((inputMeasurement) => {
          const input: MeasurementInputNode = {
            type: 'input',
            slug: inputMeasurement.id,
            name: inputMeasurement.name,
            unit: inputMeasurement.unit ?? '',
            label: measurementLabel(inputMeasurement.name, inputMeasurement.unit),
            search: measurementFuseSearchName(inputMeasurement.id, inputMeasurement.name, inputMeasurement.unit),
            parent,
          };
          parent.children.set(inputMeasurement.id, input);
          acc.inputs.set(inputMeasurement.id, input);
        });
        return acc;
      }
      return acc;
    },
    {
      outputs: new Map<string, MeasurementOutputNode>(),
      inputs: new Map<string, MeasurementInputNode>(),
    }
  );

  return { outputs, inputs };
};

export interface ListItemProps {
  icon: IconType;
  children: React.ReactNode;
}

export const ListItem: FC<ListItemProps> = ({ icon, children }) => (
  <li className="mb2">
    <Icon icon={icon} viewBox={'0 0 22 22'} width="20" height="20" className={`mr3 v-mid ${icons[icon]}`} />
    <div className="dib v-mid lh-copy w-80">{children}</div>
  </li>
);

export interface ListItemDependencyProps {
  dependentValidation: DependentValidation | undefined;
  dependency: string;
}

export const ListItemDependency: FC<ListItemDependencyProps> = ({ dependentValidation, dependency }) => {
  const icon = dependentValidation?.inTargetPreset || dependentValidation?.addedToPreset ? 'success' : 'warning';
  const dependencyStatusString = dependentValidation?.inTargetPreset
    ? ' - In target preset'
    : dependentValidation?.addedToPreset
      ? ' - Added to preset'
      : ' - Does not exist in target study';
  return (
    <ListItem icon={icon}>
      <p className="near-black lh-copy v-mid f6">
        {dependency} {dependencyStatusString}
      </p>
    </ListItem>
  );
};

const icons: Record<string, string> = {
  success: 'green',
  warning: 'yellow',
  critical: 'red',
};

export type DependentValidation = {
  inTargetPreset: boolean;
  addedToPreset: boolean;
};

export type DependenciesValidation = {
  isValid: boolean;
  dependenciesValidation: Record<string, DependentValidation>;
};

export const validateDependencies = (
  targetContainsSlug: boolean,
  dependencies: Array<string>,
  targetPresetData: TargetPresetData,
  formMethods: UseFormReturn<MeasurementMapFormData, MeasurementMapFormContext, MeasurementMapFormData>
): DependenciesValidation => {
  const dependenciesValidation: Record<string, DependentValidation> = dependencies.reduce<
    Record<string, DependentValidation>
  >((acc, dependency) => {
    const inTargetPreset = targetPresetData.options.get(dependency) !== undefined;
    const addedToPreset = formMethods.getValues(dependency) === 'addToPreset';
    acc[dependency] = { inTargetPreset: inTargetPreset, addedToPreset: addedToPreset };
    return acc;
  }, {});

  const isValid =
    !targetContainsSlug &&
    Object.values(dependenciesValidation).every((dep) => dep.inTargetPreset || dep.addedToPreset);

  return { isValid: isValid, dependenciesValidation: dependenciesValidation };
};

export const generateDefaultValues = async (
  sourceNodes: Array<MeasurementNode>,
  outputFuse: Fuse<MeasurementFuse>,
  options: Map<string, MeasurementOutputOption>,
  mappedMeasurements?: MeasurementMapping
): Promise<Record<string, string | null>> => {
  if (_notNil(mappedMeasurements) && mappedMeasurements.size > 0) {
    const values: Record<string, string | null> = {};

    mappedMeasurements.forEach((node) => {
      values[node.source_slug] = node.target_slug ?? null;
    });
    return values;
  }
  return sourceNodes.reduce<Record<string, string | null>>((acc, node) => {
    if (node.type === 'output') {
      return {
        ...acc,
        [node.slug]: node.calculatedMeasurement ? '' : (outputFuse.search(node.slug)?.at(0)?.item?.slug ?? null),
      };
    }
    if (node.type === 'input') {
      const parentTargetSlug = acc?.[node.parent.slug];
      if (_notNil(parentTargetSlug)) {
        return {
          ...acc,
          [node.slug]: options.get(parentTargetSlug)?.fuse?.search(node.slug)?.at(0)?.item?.slug ?? null,
        };
      }
    }
    return acc;
  }, {});
};

export const useMapMeasurementsResolver = (
  slugCountUpdate: (value: Map<string, number>) => void
): Resolver<MeasurementMapFormData, MeasurementMapFormContext> => {
  return useCallback(
    (values, context, options) => {
      const slugCounts = Object.values(values).reduce((acc, slug) => {
        if (typeof slug === 'string' && slug.trim() !== '') {
          acc.set(slug, (acc.get(slug) ?? 0) + 1);
        }
        return acc;
      }, new Map<string, number>());
      const errors: FieldErrors<MeasurementMapFormData> = {};

      context?.sourceOutputs?.forEach((outputNode) => {
        // Ignore validation for calculated measurements
        if (outputNode.calculatedMeasurement) {
          return;
        }
        if (_isEmptyString(values?.[outputNode.slug] ?? '')) {
          errors[outputNode.slug] = {
            type: 'required',
            message: `Mapping required. Select an option from the dropdown`,
          };
        } else if (values?.[outputNode.slug] === 'addToPreset' && outputNode.targetContainsSlug) {
          errors[outputNode.slug] = {
            type: 'non-unique',
            message: `Measurement or its inputs contain matching name in target preset`,
          };
        } else if (
          (slugCounts.get(values?.[outputNode.slug] ?? '') ?? 0) > 1 &&
          values?.[outputNode.slug] !== 'addToPreset'
        ) {
          errors[outputNode.slug] = { type: 'non-unique', message: `Measurement mapping non-unique` };
        }
        outputNode.children.forEach((inputNode) => {
          if (_isEmptyString(values?.[inputNode.slug] ?? '') && values?.[outputNode.slug] !== 'addToPreset') {
            errors[inputNode.slug] = {
              type: 'required',
              message: `Mapping required. Select an option from the dropdown`,
            };
          } else if (
            (slugCounts.get(values?.[inputNode.slug] ?? '') ?? 0) > 1 &&
            values?.[inputNode.slug] !== 'dropMeasurement'
          ) {
            errors[inputNode.slug] = { type: 'non-unique', message: `Measurement variable mapping non-unique` };
          }
        });
      });

      slugCountUpdate(slugCounts);
      return Object.keys(errors).length === 0 ? { values, errors: {} } : { values: {}, errors };
    },
    [slugCountUpdate]
  );
};

export const targetContainsSlugMessage = 'Target preset contains measurement with matching measurement id';

export const targetDoesNotContainsSlugMessage = 'Target preset does not contain matching measurement id';

export const extractTargetPresetSlugs = (presetData: TargetPresetData): Set<string> => {
  return new Set(
    [...presetData.options.values()].flatMap((option) => [
      option.slug,
      ...[...option.children.values()].map((child) => child.slug),
    ])
  );
};

export const outputOrChildrenContainsSlug = (
  calculation: StudySettingsCalculationV1,
  targetPresetSlugs: Set<string>
): boolean => {
  const slugs = [calculation.id, ...(calculation.measurements?.map((measurement) => measurement.id) ?? [])];

  return slugs.some((slug) => targetPresetSlugs.has(slug));
};

const foodIntakeMeasurement = {
  name: ANIMAL_FOOD_INTAKE.name + ' (' + ANIMAL_FOOD_INTAKE.unit + ')',
  variable: {
    add_to_preset: true,
    to: ANIMAL_FOOD_INTAKE.id,
    from: ANIMAL_FOOD_INTAKE.id,
    inputs: [],
  },
};

const fluidIntakeMeasurement = {
  name: ANIMAL_FLUID_INTAKE.name + ' (' + ANIMAL_FLUID_INTAKE.unit + ')',
  variable: {
    add_to_preset: true,
    to: ANIMAL_FLUID_INTAKE.id,
    from: ANIMAL_FLUID_INTAKE.id,
    inputs: [],
  },
};

export const createPayload = (
  data: MeasurementMapFormData,
  fromStudyApiId: StudyApiId,
  toStudyApiId: StudyApiId,
  animals: Array<AnimalApiId>,
  sourcePresetData: FromPresetData
) => {
  const measurementsToBeDropped = new Map();
  const measurementsToBeAdded = new Array<string>();
  const animalFluidIntakeAddedToPreset = data['animal_fluid_intake'] === 'addToPreset';
  const animalFoodIntakeAddedToPreset = data['animal_food_intake'] === 'addToPreset';
  const variables = [...sourcePresetData.outputs.values()]
    .map((outputNode) => {
      // Omit calculated measurements not being added from payload
      if (data[outputNode.slug] !== 'addToPreset' && outputNode.calculatedMeasurement) {
        return null;
      }
      if (data[outputNode.slug] === 'addToPreset') {
        measurementsToBeAdded.push(outputNode.label);
      }
      return {
        from: outputNode.slug,
        to: data[outputNode.slug] === 'addToPreset' ? outputNode.slug : (data[outputNode.slug] ?? ''),
        add_to_preset: data[outputNode.slug] === 'addToPreset',
        inputs: [...outputNode.children.values()].map((inputNode) => {
          if (data[inputNode.slug] === 'dropMeasurement') {
            if (!measurementsToBeDropped.has(outputNode.label)) {
              measurementsToBeDropped.set(outputNode.label, []);
            }
            measurementsToBeDropped.get(outputNode.label)?.push(inputNode.label);
          }
          return {
            from: inputNode.slug,
            to:
              data[inputNode.slug] === 'dropMeasurement' || data[outputNode.slug] === 'addToPreset'
                ? inputNode.slug
                : (data[inputNode.slug] ?? ''),
            drop_measurement: data[inputNode.slug] === 'dropMeasurement',
          };
        }),
      };
    })
    .filter(Boolean);

  // Ensure that both consumption metrics are added together
  const onlyOneConsumptionIntakeAdded = animalFluidIntakeAddedToPreset !== animalFoodIntakeAddedToPreset;
  if (onlyOneConsumptionIntakeAdded) {
    if (animalFluidIntakeAddedToPreset) {
      measurementsToBeAdded.push(foodIntakeMeasurement.name);
      variables.push(foodIntakeMeasurement.variable);
    } else {
      measurementsToBeAdded.push(fluidIntakeMeasurement.name);
      variables.push(fluidIntakeMeasurement.variable);
    }
  }

  return {
    payload: {
      old_study_api_id: fromStudyApiId,
      new_study_api_id: toStudyApiId,
      animal_api_ids: animals,
      variables: variables,
    },
    measurementsToBeDropped: measurementsToBeDropped,
    measurementsToBeAdded: measurementsToBeAdded,
  };
};
